/****************************************************************************************************
 * COMPONENT: EDIT
 * --------------------------------------------------------------------------------------------------
 * This component is used to provide quick access to Sections and Blocks for editing.
 * It is only displayed in dev mode when ?dev is present in the url query string.
 * *************************************************************************************************/
import React from 'react';
import { useDevMode } from 'src/hooks';
import { SpeedDial, SpeedDialAction, useTheme, Tooltip } from '@mui/material';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import EditIcon from '@mui/icons-material/Edit';

export interface EditProps {
  documentType: 'sectionBlock' | 'section';
  documentId: string;
}

const actions = [
  { icon: <EditIcon sx={{ color: 'white' }} />, name: 'Edit Local', domain:'http://localhost:3333' },
  { icon: <EditIcon sx={{ color: 'white' }} />, name: 'Edit Sanity Studio', domain:'https://elocms.sanity.studio' }
];

const Edit = ({ documentType, documentId }: EditProps) => {
  const isDev = useDevMode();
  const theme = useTheme(); // Use theme to access color values

  // Do not display this component if not in development mode
  if (!isDev) return null;

  const handleClicked = (domain:string): void => {
    if (typeof window !== 'undefined') {
      window.open(
        `${domain}/structure/allDocuments;${documentType};${documentId}`,
        '_studio',
      );
    }
  };

  return (
    <Tooltip
      title={documentType === 'sectionBlock' ? 'Edit Block' : 'Edit Section'}
      placement={'bottom-start'}
    >
      <SpeedDial
        ariaLabel="Edit"
        icon={<SpeedDialIcon />}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: documentType === 'sectionBlock' ? 16 : 'auto',
          left: documentType === 'section' ? 16 : 'auto',
          '& .MuiFab-root': {
            //   backgroundColor: documentType === 'sectionBlock' ? theme.palette.primary.main : theme.palette.secondary.main, // Use theme colors dynamically
            backgroundColor: documentType === 'sectionBlock' ? 'orange' : 'green', // Use theme colors dynamically
            opacity: '0.5',
            width: '35px', // Adjust width as needed
            height: '35px', // Correct height for uniformity
            '& .MuiSpeedDialIcon-root': {
              fontSize: '24px', // Adjusted for visual consistency
            },
          },
        }}
      >
        {actions.map(action => (          
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => handleClicked(action.domain)}
            />                    
        ))}
      </SpeedDial>
    </Tooltip>
  );
};

export default Edit;
